import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { Boxes, Search } from "lucide-react";
import { api, unwrapManifest, type CatalogEntry } from "../../lib/api";
import { useAsync } from "../../lib/hooks";
import { formatDuration, formatMoney, formatNumber, timeAgo } from "../../lib/format";
import { Badge, Card, CardBody, CardHeader, Checkbox, CodeBlock, DatasetScore, EmptyState, ErrorNote, FreshnessBadge, Input, LatencyBadge, ScorePill, Select, Skeleton, Table, Td, Th } from "../../components/ui";

const CATEGORIES = ["", "retail", "energy", "finance", "real-estate", "supply-chain", "travel", "mobility", "market-data", "public-sector"];

export function Marketplace() {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("");
  const [country, setCountry] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [minQuality, setMinQuality] = useState("0");
  const [maxFresh, setMaxFresh] = useState("");
  const [realtime, setRealtime] = useState(false);
  const [exclusive, setExclusive] = useState(false);
  const [freeSample, setFreeSample] = useState(false);
  const [provider, setProvider] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const { data, error, loading } = useAsync(
    () => api.public.marketplace({ q: debounced, category, country: country.trim() || undefined, maxPrice: maxPrice || undefined, minQuality: Number(minQuality) || undefined, realtime: realtime || undefined }),
    [debounced, category, country, maxPrice, minQuality, realtime],
  );

  const rows = useMemo(() => {
    let r = data?.datasets ?? [];
    if (exclusive) r = r.filter((d) => d.scores.exclusivity >= 0.8);
    if (freeSample) r = r.filter((d) => d.freeSample);
    if (provider.trim()) r = r.filter((d) => d.providerName.toLowerCase().includes(provider.trim().toLowerCase()));
    if (maxFresh) r = r.filter((d) => d.freshnessTargetSeconds !== null && d.freshnessTargetSeconds <= Number(maxFresh));
    return r;
  }, [data, exclusive, freeSample, provider, maxFresh]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <h1 className="text-3xl font-semibold tracking-tight">Data marketplace</h1>
      <p className="mt-2 max-w-2xl text-fg-2">Live, sanitised data products published by organisations on the DBIndex network. Every listing is backed by a signed manifest.</p>
      <div className="mt-8 grid gap-8 lg:grid-cols-[260px_1fr]">
        <aside className="space-y-4" aria-label="Filters">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-fg-3" aria-hidden />
            <Input type="search" role="searchbox" aria-label="Search datasets" placeholder="Search datasets…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" />
          </div>
          <FilterBlock label="Category">
            <Select aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)} options={CATEGORIES.map((c) => ({ value: c, label: c ? c.replace("-", " ") : "All categories" }))} />
          </FilterBlock>
          <FilterBlock label="Country / region">
            <Input aria-label="Country or region" placeholder="e.g. AU, AU-NSW, Sydney" value={country} onChange={(e) => setCountry(e.target.value)} />
          </FilterBlock>
          <FilterBlock label="Provider">
            <Input aria-label="Provider" placeholder="Provider name" value={provider} onChange={(e) => setProvider(e.target.value)} />
          </FilterBlock>
          <FilterBlock label="Max freshness target">
            <Select
              aria-label="Max freshness"
              value={maxFresh}
              onChange={(e) => setMaxFresh(e.target.value)}
              options={[
                { value: "", label: "Any" },
                { value: "10", label: "≤ 10 seconds" },
                { value: "60", label: "≤ 1 minute" },
                { value: "900", label: "≤ 15 minutes" },
                { value: "86400", label: "≤ 1 day" },
              ]}
            />
          </FilterBlock>
          <FilterBlock label="Max price per query (USD)">
            <Input aria-label="Max price per query" inputMode="decimal" placeholder="0.002" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
          </FilterBlock>
          <FilterBlock label={`Min quality: ${Number(minQuality).toFixed(2)}`}>
            <input type="range" aria-label="Minimum quality" min={0} max={1} step={0.05} value={minQuality} onChange={(e) => setMinQuality(e.target.value)} className="w-full accent-[var(--accent)]" />
          </FilterBlock>
          <div className="space-y-2.5 pt-1">
            <Checkbox label="Real-time (≤ 60s)" checked={realtime} onChange={setRealtime} />
            <Checkbox label="Exclusive / rare" checked={exclusive} onChange={setExclusive} />
            <Checkbox label="Free sample available" checked={freeSample} onChange={setFreeSample} />
          </div>
        </aside>
        <div className={loading && data ? "opacity-60 transition-opacity" : ""}>
          <ErrorNote error={error} className="mb-4" />
          {loading && !data ? (
            <div className="grid gap-4 md:grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-44" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Boxes className="size-8" />}
              title={data?.datasets.length ? "No datasets match these filters" : "No published datasets yet"}
              description={data?.datasets.length ? "Try widening your filters." : "When providers activate data products they appear here with their signed manifests."}
            />
          ) : (
            <>
              <p className="mb-3 text-xs text-fg-3">{rows.length} data products</p>
              <div className="grid gap-4 md:grid-cols-2">
                {rows.map((d) => (
                  <DatasetCard key={d.datasetId} d={d} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-medium text-fg-2">{label}</div>
      {children}
    </div>
  );
}

function DatasetCard({ d }: { d: CatalogEntry }) {
  return (
    <Link to={`/marketplace/${d.datasetId}`} className="group block rounded-lg border border-border bg-panel p-5 transition-colors hover:border-border-strong">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-fg group-hover:text-accent">{d.name}</h2>
          <p className="text-xs text-fg-3">{d.providerName}</p>
        </div>
        <span className="tabular shrink-0 text-sm font-medium">
          {formatMoney(d.pricePerQueryMicros)}
          <span className="text-xs text-fg-3">/query</span>
        </span>
      </div>
      <p className="mt-2 line-clamp-2 text-sm text-fg-2">{d.description}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {d.categories.slice(0, 3).map((c) => (
          <Badge key={c}>{c}</Badge>
        ))}
        {d.geographic.slice(0, 2).map((g) => (
          <Badge key={g} tone="info">
            {g}
          </Badge>
        ))}
        {d.freshnessTargetSeconds !== null && <Badge tone="accent">target {formatDuration(d.freshnessTargetSeconds)}</Badge>}
        {d.freeSample && <Badge tone="good">free sample</Badge>}
      </div>
      <div className="mt-4 flex items-center gap-4 text-xs text-fg-3">
        <span>
          quality <ScorePill value={d.scores.quality} />
        </span>
        <span>{d.capabilities.length} capabilities</span>
        <span>~{d.estimatedLatencyMs}ms</span>
      </div>
    </Link>
  );
}

export function MarketplaceDataset() {
  const { dataset = "" } = useParams();
  const { data, error, loading } = useAsync(() => api.public.dataset(dataset), [dataset]);
  const m = unwrapManifest(data?.manifest);
  if (loading && !data)
    return (
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <Skeleton className="h-8 w-80" />
        <Skeleton className="mt-6 h-64" />
      </div>
    );
  if (error || !m)
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <ErrorNote error={error ?? { message: "Dataset not found." }} />
      </div>
    );
  const st = data?.status;
  const sampleQuery = m.capabilities[0]
    ? JSON.stringify({ plan: { datasetId: m.datasetId, capability: m.capabilities[0].name, filters: {}, limit: 10 } }, null, 2)
    : "";
  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <Link to="/marketplace" className="text-xs text-fg-3 hover:text-fg">
        ← Marketplace
      </Link>
      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{m.name}</h1>
          <p className="mt-1 text-sm text-fg-2">
            by <span className="text-fg">{m.providerName}</span> · v{m.schemaVersion} · contract v{m.contractVersion} · published {timeAgo(m.publishedAt)}
          </p>
        </div>
        <div className="text-right">
          <div className="tabular text-2xl font-semibold">{formatMoney(m.pricing.pricePerQueryMicros)}</div>
          <div className="text-xs text-fg-3">per query · USD</div>
        </div>
      </div>
      <p className="mt-4 max-w-3xl text-fg-2">{m.description}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <FreshnessBadge lagSeconds={st?.lagSeconds ?? m.freshness.avgActualLagSeconds} targetSeconds={m.freshness.targetSeconds} />
        <LatencyBadge ms={m.estimatedLatencyMs} label="est." />
        {m.categories.map((c) => (
          <Badge key={c}>{c}</Badge>
        ))}
        {m.coverage.geographic.map((g) => (
          <Badge key={g} tone="info">
            {g}
          </Badge>
        ))}
        {data?.competition && <Badge tone={data.competition.similarCount > 5 ? "neutral" : "accent"}>{data.competition.similarCount} similar providers</Badge>}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <Card>
            <CardHeader title="Capabilities" description="Typed operations available to consumers. Anything else is rejected." />
            <CardBody className="space-y-4">
              {m.capabilities.map((c) => (
                <div key={c.name} className="rounded-md border border-border bg-bg-elev p-3">
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-sm text-accent">{c.name}</code>
                    <Badge>{c.kind}</Badge>
                    <span className="text-xs text-fg-3">max {c.maxRows} rows</span>
                  </div>
                  <p className="mt-1 text-sm text-fg-2">{c.description}</p>
                  {c.filters.length > 0 && (
                    <p className="mt-2 text-xs text-fg-3">
                      Filters:{" "}
                      {c.filters.map((f) => (
                        <code key={f.field} className="mr-2 font-mono text-fg-2">
                          {f.field}
                          {f.required ? "*" : ""} [{f.ops.join(",")}]
                        </code>
                      ))}
                    </p>
                  )}
                  {c.examples.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-xs text-fg-2">
                      {c.examples.map((e) => (
                        <li key={e}>“{e}”</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Schema" description="Only these sanitised serving fields exist. Source tables are never exposed." />
            {m.entities.map((e) => (
              <div key={e.name} className="border-b border-border last:border-0">
                <div className="px-4 pt-3 font-mono text-sm text-fg">{e.name}</div>
                <Table>
                  <thead>
                    <tr>
                      <Th>Field</Th>
                      <Th>Type</Th>
                      <Th>Description</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {e.fields.map((f) => (
                      <tr key={f.name}>
                        <Td mono>{f.name}</Td>
                        <Td mono className="text-fg-2">
                          {f.type}
                        </Td>
                        <Td className="text-fg-2">{f.description ?? "—"}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ))}
          </Card>
          {sampleQuery && <CodeBlock title="Sample structured query (POST /v1/query)" code={sampleQuery} />}
        </div>
        <div className="space-y-6">
          <Card>
            <CardHeader title="Scores" />
            <CardBody>
              <DatasetScore scores={m.scores} />
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Live status" />
            <CardBody className="space-y-2 text-sm">
              <Row k="Rows" v={st ? formatNumber(st.rowCount) : formatNumber(m.coverage.rowCountApprox)} />
              <Row k="Last update" v={timeAgo(st?.lastAppliedAt ?? m.freshness.lastUpdatedAt)} />
              <Row k="Freshness target" v={formatDuration(m.freshness.targetSeconds)} />
              <Row k="Historical from" v={m.coverage.historicalFrom ?? "—"} />
              <Row k="Jurisdiction" v={m.jurisdiction} />
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Licence & terms" description={m.rights.licenseId} />
            <CardBody className="space-y-1.5 text-sm">
              {(
                [
                  ["Live query", m.rights.liveQuery],
                  ["RAG", m.rights.rag],
                  ["Batch data", m.rights.batchData],
                  ["Model training", m.rights.training],
                  ["Commercial use", m.rights.commercialUse],
                  ["Display to end user", m.rights.displayToEndUser],
                  ["Redistribution", m.rights.redistribution],
                  ["Attribution required", m.rights.attributionRequired],
                ] as const
              ).map(([k, v]) => (
                <Row key={k} k={k} v={<Badge tone={v ? "good" : "neutral"}>{v ? "allowed" : "not allowed"}</Badge>} />
              ))}
              <Row k="Max retention" v={formatDuration(m.rights.maxRetentionSeconds)} />
              <p className="pt-2 text-xs text-fg-3">Paying for a query does not grant model-training rights unless training is explicitly allowed.</p>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-fg-3">{k}</span>
      <span className="tabular text-right text-fg">{v}</span>
    </div>
  );
}
